import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const Auth: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | 'twitter' | null>(null);

  const { signIn, signUp, resetPassword, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        toast.error('Invalid email or password');
      } else {
        toast.error(error.message);
      }
    } else {
      toast.success('Welcome back.');
      navigate('/dashboard');
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !fullName) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    const { error } = await signUp(email, password, fullName);
    if (error) {
      if (error.message.includes('User already registered')) {
        toast.error('An account with this email already exists');
      } else {
        toast.error(error.message);
      }
    } else {
      toast.success('Account created. Check your email to confirm.');
    }
    setLoading(false);
  };

  const handleOAuth = async (provider: 'google' | 'twitter') => {
    setOauthLoading(provider);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) {
        const label = provider === 'twitter' ? 'X' : 'Google';
        toast.error(`${label} sign-in failed: ${error.message}`);
      }
    } finally {
      setOauthLoading(null);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Enter your email above first.');
      return;
    }
    setLoading(true);
    const { error } = await resetPassword(email);
    if (error) toast.error(error.message);
    else toast.success('Password reset email sent. Check your inbox.');
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 font-sans"
      style={{ backgroundColor: '#FAFAF8' }}
    >
      <div className="w-full max-w-md">
        <div className="bg-background border border-border rounded-2xl px-8 py-10 sm:px-10">
          <div className="text-center mb-8">
            <Link
              to="/"
              className="inline-block text-2xl font-semibold tracking-tight text-foreground"
            >
              SocialScoop
            </Link>
          </div>

          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-transparent p-0 mb-8 border-b border-border rounded-none h-auto">
              <TabsTrigger
                value="signin"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground py-3 text-sm font-medium"
              >
                Sign in
              </TabsTrigger>
              <TabsTrigger
                value="signup"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground py-3 text-sm font-medium"
              >
                Create account
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="mt-0">
              <h1 className="text-2xl font-semibold tracking-tight mb-6">Welcome back.</h1>

              <OAuthButtonGroup oauthLoading={oauthLoading} onSelect={handleOAuth} />

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center" aria-hidden>
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-background px-3 text-xs uppercase tracking-wider text-muted-foreground">
                    Or with email
                  </span>
                </div>
              </div>

              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signin-password">Password</Label>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                    >
                      Forgot?
                    </button>
                  </div>
                  <Input
                    id="signin-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <Button type="submit" className="w-full h-11" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-0">
              <h1 className="text-2xl font-semibold tracking-tight mb-2">Create your account.</h1>
              <p className="text-sm text-muted-foreground mb-5">Free to start. No credit card.</p>

              {/* What's included in free */}
              <div className="rounded-xl border border-border bg-muted/30 px-4 py-4 mb-6">
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Free includes</p>
                <ul className="space-y-2">
                  {[
                    'Bluesky + YouTube + RSS / Newsletter feeds',
                    'Up to 5 Listening Rules + 5 Fences',
                    'AI content scoring on every post',
                    'Public curator profile',
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <span className="text-foreground/40 mt-0.5">✓</span>
                      <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Want Reddit + X and 20 rules?{' '}
                    <span className="font-medium text-foreground">Try everything for $1 — 30 days full access.</span>
                    {' '}Sign up free first, then upgrade inside your dashboard.
                  </p>
                </div>
              </div>

              <OAuthButtonGroup oauthLoading={oauthLoading} onSelect={handleOAuth} />

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center" aria-hidden>
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-background px-3 text-xs uppercase tracking-wider text-muted-foreground">
                    Or with email
                  </span>
                </div>
              </div>

              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="signup-name">Full name</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">At least 6 characters.</p>
                </div>
                <Button type="submit" className="w-full h-11" disabled={loading}>
                  {loading ? 'Creating account…' : 'Create free account'}
                </Button>
              </form>

              <p className="text-xs text-muted-foreground text-center mt-5 leading-relaxed">
                By creating an account you agree to our{' '}
                <a href="/terms" className="underline underline-offset-4 hover:text-foreground">Terms</a>
                {' '}and{' '}
                <a href="/privacy" className="underline underline-offset-4 hover:text-foreground">Privacy Policy</a>.
              </p>
            </TabsContent>
          </Tabs>
        </div>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

const GoogleIcon: React.FC = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const XIcon: React.FC = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
  </svg>
);

interface OAuthButtonGroupProps {
  oauthLoading: 'google' | 'twitter' | null;
  onSelect: (provider: 'google' | 'twitter') => void;
}

const OAuthButtonGroup: React.FC<OAuthButtonGroupProps> = ({ oauthLoading, onSelect }) => {
  return (
    <div className="space-y-3 mb-6">
      <Button
        type="button"
        variant="outline"
        className="w-full h-11 gap-2"
        onClick={() => onSelect('google')}
        disabled={oauthLoading !== null}
      >
        <GoogleIcon />
        {oauthLoading === 'google' ? 'Connecting…' : 'Continue with Google'}
      </Button>
      <Button
        type="button"
        variant="outline"
        className="w-full h-11 gap-2"
        onClick={() => onSelect('twitter')}
        disabled={oauthLoading !== null}
      >
        <XIcon />
        {oauthLoading === 'twitter' ? 'Connecting…' : 'Continue with X'}
      </Button>
    </div>
  );
};

export default Auth;
